<p-confirmDialog />

<!-- Toast Messages for embedded/web component mode only -->
@if (embeddedMode) {
  <p-toast
    position="top-right"
    [breakpoints]="{ '920px': { width: '100%', right: '0', left: '0' } }"
  />
}

<div class="edit-scheduled-order-page">
  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-container">
    <!-- Left Sidebar Skeleton -->
    <div class="skeleton-left-sidebar">
      <!-- Collapse toggle skeleton -->
      <div class="skeleton-sidebar-header">
        <p-skeleton width="2rem" height="2rem" borderRadius="0.5rem" />
      </div>

      <!-- Step skeletons -->
      <div class="skeleton-steps">
        <div class="skeleton-step" *ngFor="let step of [1, 2, 3, 4, 5, 6]">
          <div class="skeleton-step-content">
            <p-skeleton shape="circle" size="2rem" />
            <div class="skeleton-step-text">
              <p-skeleton width="6rem" height="1rem" />
            </div>
          </div>
        </div>
      </div>

      <!-- Action buttons skeleton -->
      <div class="skeleton-actions">
        <p-skeleton width="100%" height="2.5rem" borderRadius="0.5rem" />
        <p-skeleton width="100%" height="2.5rem" borderRadius="0.5rem" />
      </div>
    </div>

    <!-- Center Content Skeleton -->
    <div class="skeleton-center-content">
      <!-- Section banner skeleton -->
      <div class="skeleton-banner">
        <p-skeleton width="60%" height="1.5rem" styleClass="mb-2" />
        <p-skeleton width="80%" height="1rem" />
      </div>

      <!-- Content wrapper skeleton -->
      <div class="skeleton-content-wrapper">
        <!-- Form fields -->
        <div class="skeleton-form-section">
          <p-skeleton width="8rem" height="1rem" styleClass="mb-2" />
          <p-skeleton width="100%" height="2.5rem" borderRadius="0.5rem" />
        </div>

        <div class="skeleton-form-section">
          <p-skeleton width="10rem" height="1rem" styleClass="mb-2" />
          <p-skeleton width="100%" height="2.5rem" borderRadius="0.5rem" />
        </div>

        <div class="skeleton-form-section">
          <p-skeleton width="9rem" height="1rem" styleClass="mb-2" />
          <p-skeleton width="100%" height="4rem" borderRadius="0.5rem" />
        </div>
      </div>
    </div>

    <!-- Right Sidebar Skeleton (hidden on mobile) -->
    <div class="skeleton-right-sidebar">
      <p-skeleton width="60%" height="1.25rem" styleClass="mb-3" />
      <!-- Totals skeleton -->
      <div class="skeleton-totals">
        <div class="skeleton-total-row" *ngFor="let row of [1, 2, 3, 4, 5]">
          <p-skeleton width="6rem" height="1rem" />
          <p-skeleton width="4rem" height="1rem" />
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content -->
  <div *ngIf="!isLoading && formGroup && scheduledOrder" class="page-content">
    <!-- Top Header with Actions -->
    <div class="page-header">
      <div class="header-left">
        <!-- Item 17: Back button - icon only at top (no label) -->
        <p-button
          icon="pi pi-arrow-left"
          [text]="true"
          (onClick)="goBackToList()"
          pTooltip="Back to list"
          tooltipPosition="bottom"
          styleClass="back-to-list-btn"
        />
        <h3 class="page-title">#{{ scheduledOrder.Id }}</h3>
        <p-tag
          [value]="
            scheduledOrder.Status +
            (scheduledOrder.IsInDunning ? ' in Dunning' : '')
          "
          [severity]="getStatusSeverity(scheduledOrder.Status)"
        />
        <!-- Progress indicator for operations -->
        <p-progressBar
          *ngIf="isProcessing"
          mode="indeterminate"
          styleClass="progress-indicator"
        />
      </div>
      <div class="header-right">
        <!-- Item 13: Quick actions toggle removed - actions always visible -->

        <!-- Mobile Save Button (only visible ≤768px) -->
        <p-button
          icon="pi pi-save"
          [rounded]="true"
          [text]="true"
          [disabled]="isSaving || isProcessing"
          [loading]="isSaving"
          (onClick)="update()"
          pTooltip="Save changes"
          tooltipPosition="bottom"
          styleClass="mobile-save-button"
        />

        <!-- Mobile Totals Popover Button (only visible ≤768px) -->
        <p-button
          icon="pi pi-dollar"
          [rounded]="true"
          [text]="true"
          (onClick)="totalsPopover.toggle($event)"
          pTooltip="View totals"
          tooltipPosition="bottom"
          styleClass="mobile-totals-button"
        />
        <!-- Item 14: Added close button to totals popover -->
        <p-popover #totalsPopover styleClass="totals-popover">
          <ng-template pTemplate="content">
            <div class="popover-totals-container">
              <div class="popover-header">
                <h4 class="popover-totals-title">Order Total Summary</h4>
                <p-button
                  icon="pi pi-times"
                  [rounded]="true"
                  [text]="true"
                  size="small"
                  (onClick)="totalsPopover.hide()"
                  pTooltip="Close"
                  tooltipPosition="left"
                  styleClass="popover-close-btn"
                />
              </div>
              <app-totals-display
                *ngIf="formComponent"
                [totals]="formComponent.totals"
                [isLoading]="formComponent.isCalculatingTotals"
                [showTitle]="false"
                [hasDisabledItems]="formComponent.hasDisabledItems()"
              />
            </div>
          </ng-template>
        </p-popover>

        <p-button
          icon="pi pi-ellipsis-v"
          [rounded]="true"
          [text]="true"
          (onClick)="actionsMenu.toggle($event)"
          pTooltip="Actions"
          tooltipPosition="bottom"
        />
        <p-menu
          #actionsMenu
          [model]="actionMenuItems"
          [popup]="true"
          appendTo="body"
        />
      </div>
    </div>

    <!-- Quick Actions Bar (below stepper tabs) -->
    <!-- Hide quick actions for Processing status only (operations not possible during processing) -->
    <!-- Deleted orders CAN show quick actions (e.g., Activate to restore) -->
    <!-- Item 13: Always show quick actions (removed toggle/collapse behavior) -->
    <div
      *ngIf="scheduledOrder.Status !== 'Processing'"
      class="quick-actions-container"
    >
      <app-scheduled-order-quick-actions
        [order]="scheduledOrder"
        [displayMode]="'horizontal'"
        [loading]="isSaving || isProcessing"
        [filterStyle]="true"
        (actionClick)="onQuickAction($event)"
      />
    </div>

    <!-- Form with Sidebar (handles its own actions and validation as Step 0) -->
    <div class="form-container">
      <app-scheduled-order-form
        [formGroup]="formGroup"
        [scheduledOrder]="scheduledOrder"
        [siteId]="siteId"
        [site]="site"
        [mode]="'edit'"
        [disabled]="isSaving || isProcessing"
        [isSaving]="isSaving"
        [showActions]="true"
        [showValidation]="showValidationIndicator"
        [advancedOptionsAllowed]="advancedOptionsAllowed"
        [tierName]="tierName"
        [upgradeUrl]="upgradeUrl"
        [hasNextime]="hasNextime"
        [nextimeSiteId]="nextimeSiteId"
        [nextimeSiteToken]="nextimeSiteToken"
        [nextimeShouldDisplayDeliveryDate]="nextimeShouldDisplayDeliveryDate"
        [nextimeShouldAlignNextOrderDate]="nextimeShouldAlignNextOrderDate"
        [nextimeShippingOptions]="nextimeShippingOptions"
        [accessToken]="getAccessToken()"
        (formCancel)="cancel()"
        (formSubmit)="update()"
        (validationRefreshComplete)="refreshValidation()"
        (viewCycleDetails)="goToProcessingCycleView($event)"
        (viewProcessingLogs)="loadLogs($event)"
        (openTotalsPopover)="totalsPopover.toggle($event)"
      />
    </div>
  </div>
</div>
